import { type Component, type ComponentOptions, defineAsyncComponent, h, type Ref, shallowRef } from "vue";
import { CUSTOM_ELEMENT_LOAD_ERROR_ATTR } from "../constants/customElementNames";
import { type RegistryEntry, type VueCustomElementSource } from "../types/customElements";
import { registerCustomElementFromVue } from "./registerCustomElementFromVue";

const lazyLoadErrorComponent = {
  name: "Easy8LazyCustomElementLoadError",
  inheritAttrs: false,
  setup() {
    return () => h("span", { hidden: true, [CUSTOM_ELEMENT_LOAD_ERROR_ATTR]: "" });
  },
};

/** Rendered while the component chunk loads. The host's light DOM stays in place, so there is nothing to draw. */
const lazyLoadPendingComponent: Component = {
  name: "Easy8LazyCustomElementPending",
  inheritAttrs: false,
  setup() {
    return () => null;
  },
};

const isComponentLike = (value: unknown): value is Component =>
  Boolean(value) && (typeof value === "object" || typeof value === "function");

/** Normalizes one `emits` declaration; Vue accepts both the array and the object-validator form. */
const emitNames = (emits: ComponentOptions["emits"]): string[] => {
  if (Array.isArray(emits)) return emits;
  return emits ? Object.keys(emits) : [];
};

/**
 * Collects the component's own emits plus everything Vue would merge in through `extends` and `mixins`, mirroring
 * `normalizeEmitsOptions`. Without this, an event declared only by a base component never reaches the host.
 */
const collectDeclaredEmits = (component: unknown): string[] => {
  if (!isComponentLike(component)) return [];

  const options = component as ComponentOptions;
  const inherited = [...(options.mixins ?? []), ...(options.extends ? [options.extends] : [])];

  return [...inherited.flatMap(collectDeclaredEmits), ...emitNames(options.emits)];
};

// Defensive only: every current consumer of the published list tolerates duplicates, so no test can observe the
// deduplication on its own. Keep it so a future duplicate-sensitive consumer cannot regress silently.
const resolveForwardedEmits = (entry: RegistryEntry, component: unknown): string[] => {
  const discovered = entry.forwardComponentEmits === false ? [] : collectDeclaredEmits(component);
  return [...new Set([...(entry.emits ?? []), ...discovered])];
};

const resolveRegistryComponent = (entry: RegistryEntry, forwardedEmits: Ref<string[]>): VueCustomElementSource => {
  return defineAsyncComponent({
    loader: async () => {
      try {
        const module = await entry.loadComponent();
        if (!isComponentLike(module?.default)) {
          // Reported here rather than left to Vue: an "invalid async component" rejection escapes this handler, so the
          // host would render neither the component nor the error marker.
          console.error("[easy8 custom elements] Lazy custom element module has no component default export", module);
          return lazyLoadErrorComponent;
        }
        // Publish before resolving the async component so every host binds listeners before child setup.
        forwardedEmits.value = resolveForwardedEmits(entry, module.default);
        return module.default;
      } catch (error) {
        console.error("[easy8 custom elements] Failed to load lazy custom element component", error);
        return lazyLoadErrorComponent;
      }
    },
    loadingComponent: lazyLoadPendingComponent,
    delay: 0,
  }) as VueCustomElementSource;
};

const registerCustomElementFromRegistryEntry = (name: string, entry: RegistryEntry): void => {
  const forwardedEmits = shallowRef(entry.emits ?? []);
  registerCustomElementFromVue(
    resolveRegistryComponent(entry, forwardedEmits),
    name,
    entry.propTypes,
    entry.modelValueType,
    entry.slots,
    entry.exposedMethods,
    { forceWrapper: true, forwardedEmits, pendingComponent: lazyLoadPendingComponent }
  );
};

export const registerCustomElementRegistry = <Name extends string>(registry: Record<Name, RegistryEntry>): void => {
  for (const [name, entry] of Object.entries(registry) as [Name, RegistryEntry][]) {
    registerCustomElementFromRegistryEntry(name, entry);
  }
};
